import { useEffect, useState } from 'react'
import { connectionApi } from '../../api/connectionApi'
import { Connection, ConnectionRequest } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, UserCheck, UserX, UserPlus, MessageCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'

export default function ConnectionsPage() {
  const [connections, setConnections] = useState<Connection[]>([])
  const [pendingRequests, setPendingRequests] = useState<ConnectionRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'connections' | 'requests'>('connections')

  useEffect(() => {
    Promise.all([
      connectionApi.getConnections(),
      connectionApi.getPendingRequests(),
    ])
      .then(([connRes, reqRes]) => {
        setConnections(connRes.data.data?.content || [])
        setPendingRequests(reqRes.data.data?.content || [])
      })
      .catch(() => toast.error('Failed to load connections'))
      .finally(() => setLoading(false))
  }, [])

  const handleAccept = async (requestId: number) => {
    try {
      await connectionApi.acceptRequest(requestId)
      const accepted = pendingRequests.find((r) => r.id === requestId)
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId))
      if (accepted) {
        // Add to connections list (simplified)
        toast.success('Connection accepted!')
      }
    } catch {
      toast.error('Failed to accept request')
    }
  }

  const handleReject = async (requestId: number) => {
    try {
      await connectionApi.rejectRequest(requestId)
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId))
      toast.success('Request declined')
    } catch {
      toast.error('Failed to decline request')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-4">Connections</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setTab('connections')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'connections'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          My Connections ({connections.length})
        </button>
        <button
          onClick={() => setTab('requests')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors relative ${
            tab === 'requests'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Requests
          {pendingRequests.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {pendingRequests.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'connections' && (
        <div className="space-y-3">
          {connections.length === 0 ? (
            <div className="card text-center py-12 text-gray-400">
              <UserPlus size={32} className="mx-auto mb-3 opacity-30" />
              <p>No connections yet. Apply for jobs to connect with employees!</p>
            </div>
          ) : (
            connections.map((conn) => (
              <div key={conn.id} className="card flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                    {conn.otherUser.profilePicUrl ? (
                      <img src={conn.otherUser.profilePicUrl} alt={conn.otherUser.displayName} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-medium text-primary-700">
                        {conn.otherUser.displayName?.[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{conn.otherUser.displayName}</p>
                    <p className="text-xs text-gray-400">
                      Connected {formatDistanceToNow(new Date(conn.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/chat`}
                  className="btn-secondary text-xs flex items-center gap-1.5"
                >
                  <MessageCircle size={14} /> Chat
                </Link>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'requests' && (
        <div className="space-y-3">
          {pendingRequests.length === 0 ? (
            <div className="card text-center py-12 text-gray-400">No pending requests</div>
          ) : (
            pendingRequests.map((req) => (
              <div key={req.id} className="card">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                      {req.requester.profilePicUrl ? (
                        <img src={req.requester.profilePicUrl} alt={req.requester.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-medium text-primary-700">
                          {req.requester.displayName?.[0]?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{req.requester.displayName}</p>
                      <p className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(req.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAccept(req.id)}
                      className="btn-primary text-xs flex items-center gap-1"
                    >
                      <UserCheck size={14} /> Accept
                    </button>
                    <button
                      onClick={() => handleReject(req.id)}
                      className="btn-secondary text-xs flex items-center gap-1"
                    >
                      <UserX size={14} /> Decline
                    </button>
                  </div>
                </div>
                {req.message && (
                  <p className="text-sm text-gray-500 mt-2 italic">"{req.message}"</p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
